/**
 * Roster · Audit log — admin-only daily sign-in / sign-out trail.
 *
 * Tablet-friendly compact stream. Filter by date range. Calm, accountable.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import Brand from "../components/Brand";
import { ArrowLeft, LogIn, LogOut, ScrollText, Download } from "lucide-react";

function timeFull(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function RosterLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [rows, setRows] = useState([]);
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/workspaces").then((r) => {
      const list = r.data || [];
      const mine = list.find((w) => (w.admin_ids || []).includes(user.id));
      setWorkspace(mine || null);
    });
  }, [user]);

  const reload = async () => {
    if (!workspace) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/workspaces/${workspace.id}/attendance/log?date_from=${dateFrom}&date_to=${dateTo}`
      );
      setRows(r.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!workspace) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id, dateFrom, dateTo]);

  const csv = useMemo(() => {
    const head = "date,kind,child,by,pickup_person,relation,note";
    const lines = rows.map((r) =>
      [
        r.at,
        r.kind,
        r.child_name,
        r.by_user_name,
        r.pickup_person_name || "",
        r.pickup_person_relation || "",
        (r.note || "").replace(/,/g, ";"),
      ]
        .map((v) => `"${String(v).replace(/"/g, "'")}"`)
        .join(",")
    );
    return [head, ...lines].join("\n");
  }, [rows]);

  const download = () => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${dateFrom}_to_${dateTo}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6">
        <p data-testid="roster-log-forbidden" className="font-serif italic text-[#6A6863]">
          The audit log is for admins only.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="roster-log-screen" className="min-h-screen pb-32">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between flex-wrap gap-4">
        <button
          type="button"
          onClick={() => navigate("/roster")}
          data-testid="roster-log-back"
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29] transition-colors"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">Roster</span>
        </button>
        <Brand size="sm" suffix={workspace?.name || "Audit"} dataTestId="brand-roster-log" />
      </header>

      <main className="px-5 sm:px-10 pt-6 max-w-5xl mx-auto fade-up">
        <span className="eyebrow">Audit log</span>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl tracking-tight">
          Every sign-in, every sign-out.
        </h1>
        <p className="mt-2 text-sm text-[#6A6863] italic font-serif">
          Calm accountability. Human-updated by your team.
        </p>

        <section className="mt-6 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs">
            <span className="tracking-[0.18em] uppercase text-[#6A6863]">From</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="roster-log-date-from"
              className="bg-white border hairline rounded-full px-3 py-1.5 outline-none text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <span className="tracking-[0.18em] uppercase text-[#6A6863]">To</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="roster-log-date-to"
              className="bg-white border hairline rounded-full px-3 py-1.5 outline-none text-sm"
            />
          </label>
          <button
            type="button"
            onClick={download}
            disabled={rows.length === 0}
            data-testid="roster-log-export"
            className="btn-ghost !py-2 !px-4 !text-xs"
          >
            <Download size={12} strokeWidth={1.5} />
            Export CSV
          </button>
        </section>

        <section className="mt-6">
          {loading ? (
            <p className="font-serif italic text-[#6A6863]">A quiet moment…</p>
          ) : rows.length === 0 ? (
            <p
              data-testid="roster-log-empty"
              className="font-serif italic text-[#6A6863] rounded-2xl border hairline bg-white p-7"
            >
              No sign-ins or sign-outs in this range.
            </p>
          ) : (
            <ol data-testid="roster-log-list" className="space-y-2">
              {rows.map((r) => (
                <li
                  key={r.id}
                  data-testid={`roster-log-row-${r.id}`}
                  className="rounded-2xl border hairline bg-white p-4 flex items-center gap-3"
                  style={{
                    borderLeft:
                      r.kind === "arrival"
                        ? "3px solid #8A9984"
                        : "3px solid #A89CC8",
                  }}
                >
                  <span aria-hidden className="text-[#6A6863] shrink-0">
                    {r.kind === "arrival" ? (
                      <LogIn size={14} strokeWidth={1.5} />
                    ) : (
                      <LogOut size={14} strokeWidth={1.5} />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#2C2B29]">
                      <span className="font-serif">{r.child_name}</span>
                      <span className="text-[#6A6863]">
                        {r.kind === "arrival" ? " signed in by " : " signed out by "}
                      </span>
                      <span>{r.by_user_name}</span>
                      {r.pickup_person_name && (
                        <>
                          <span className="text-[#6A6863]"> with </span>
                          <span>{r.pickup_person_name}</span>
                          {r.pickup_person_relation && (
                            <span className="text-[#6A6863]">
                              {" "}
                              ({r.pickup_person_relation.replace("_", " ")})
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {r.note && (
                      <div className="mt-1 text-xs text-[#6A6863] italic font-serif">
                        {r.note}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863] shrink-0">
                    {timeFull(r.at)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>
    </div>
  );
}
